<template>
    <div class="d-inline-flex align-center">
        {{ teamName }} {{ subgroupName }}
        <v-btn @click="show" icon v-if="!disabled">
            <v-icon>mdi-chevron-down</v-icon>
        </v-btn>

        <base-dialog :loading="loading" :value="dialog" :width="460" :title="$t('common.select_team')"
            @input="val => { if (!val) hide() }" @close="hide">
            <div class="team-selects">
                <kimsa-combobox :placeholder="$t('common.select_team')" dense :items="teams" @change="team = $event;"
                    :value="team" />
                <kimsa-combobox :placeholder="$t('common.select_subgroup')" dense :items="subgroups"
                    @change="subgroup = $event" :value="subgroup" :readonly="!team" />
            </div>

            <template v-slot:footer>
                <dialog-actions :confirm-label="$t('common.save')" :disabled="isDisabled" :loading="loading"
                    @cancel="hide" @confirm="checkAssignedRoutines" />
            </template>
        </base-dialog>

        <select-replace-assigned-routines-dialog :assigned-routines-in-same-date="assignedRoutinesInSameDate" mode="dates"
            persistent is-second-dialog @confirm="onConfirmReplace($event)" @cancel="onCancelReplace"
            ref="replaceRoutinesDates" />
    </div>
</template>

<script>
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import SelectReplaceAssignedRoutinesDialog
    from "@/components/coach/routines/SelectReplaceAssignedRoutinesDialog";
import {
    addAssignedRoutinesGroup,
    deleteAssignedRoutinesGroup,
    deleteAssignedRoutineStudentInDate,
    getGroupAssignedRoutines,
    hasAssignedRoutineInDate,
    setStudentTeam
} from "@/utils/Exercises";

export default {
    name: "SelectTeamStudentMenu",
    components: { SelectReplaceAssignedRoutinesDialog, KimsaCombobox, BaseDialog, DialogActions },
    props: {
        teams: {
            type: Array,
            default: () => [],
        },
        studentId: {
            type: String,
            default: null
        },
        studentName: {
            type: String,
            default: null
        },
        currentTeam: {
            type: Object,
            default: () => {
            },
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            dialog: false,
            loading: false,
            team: null,
            subgroup: null,
            groupRoutines: [],
            assignedRoutinesInSameDate: [],
        }
    },
    computed: {
        isDisabled(vm = this) {
            if (vm.team === undefined) {
                return false
            }
            else if (vm.currentTeam) {
                return !vm.subgroup
            } else {
                return !!(!vm.team || !vm.subgroup)
            }
        },
        findTeam(vm = this) {
            return vm.teams.find(team => team.id === vm.currentTeam?.id || null) || null
        },
        findGroup(vm = this) {
            return vm.findTeam?.groups?.find(group => group.id === vm.currentTeam?.subgroupId || null) || null
        },
        teamName(vm = this) {
            return vm.findTeam?.name || vm.$t('common.without_team')
        },
        subgroupName(vm = this) {
            if (!vm.findGroup) return ''
            if (vm.findGroup.name) return `- ${vm.findGroup.name}`
            const idx = vm.findTeam?.groups?.findIndex(g => g.id === vm.findGroup.id) ?? -1
            return `- ${vm.$t('common.subgroup')} ${idx + 1}`
        },
        subgroups(vm = this) {
            return (vm.team?.groups || []).map((group, i) => ({
                ...group,
                name: group.name || `${vm.$t('common.subgroup')} ${i + 1}`
            }))
        }
    },
    mounted() {
        const vm = this
        vm.setDefaults()
    },
    methods: {
        show() {
            const vm = this
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.team = null
            vm.subgroup = null
            vm.groupRoutines = []
            vm.assignedRoutinesInSameDate = []
            vm.setDefaults()
        },
        async checkAssignedRoutines() {
            const vm = this
            vm.loading = true

            const groupRoutines = await getGroupAssignedRoutines(vm.subgroup.id, vm.team.id)
            vm.groupRoutines = groupRoutines

            if (groupRoutines.length === 0) return vm.save()

            const checks = await Promise.all(groupRoutines.map(routine =>
                hasAssignedRoutineInDate(vm.studentId, routine.rawDate.full, routine.routineNumber || 1)
            ))
            const assignedRoutinesInSameDate = checks
                .map((res, i) => res.has ? {
                    assignedRoutineId: res.id,
                    date: vm.$moment(groupRoutines[i].rawDate.full).format('DD-MM-YYYY'),
                    replace: false,
                } : null)
                .filter(Boolean)

            if (assignedRoutinesInSameDate.length > 0) {
                vm.assignedRoutinesInSameDate = assignedRoutinesInSameDate
                vm.$refs.replaceRoutinesDates.show()
            } else {
                await Promise.all(vm.groupRoutines.map(async routine => {
                    await deleteAssignedRoutineStudentInDate(vm.studentId, routine.rawDate.full, routine.routineNumber || 1)
                    await addAssignedRoutinesGroup({ ...routine, date: routine.rawDate.full }, [vm.studentId])
                }))
                await vm.save()
            }
        },
        async onConfirmReplace({ toReplace }) {
            const vm = this
            if (toReplace.length > 0) {
                const toDelete = toReplace.filter(ar => ar.replace).map(ar => ar.assignedRoutineId)
                if (toDelete.length > 0) await deleteAssignedRoutinesGroup(toDelete)

                const skipRoutinesDates = toReplace.filter(ar => !ar.replace).map(ar => ar.date)
                const toAdd = vm.groupRoutines.filter(routine =>
                    !skipRoutinesDates.includes(vm.$moment(routine.rawDate.full).format('DD-MM-YYYY'))
                )
                await Promise.all(toAdd.map(async routine => {
                    await deleteAssignedRoutineStudentInDate(vm.studentId, routine.rawDate.full, routine.routineNumber || 1)
                    await addAssignedRoutinesGroup({ ...routine, date: routine.rawDate.full }, [vm.studentId])
                }))
            }
            await vm.save()
        },
        async save() {
            const vm = this
            await vm.withLoading(async () => {
                await setStudentTeam(
                    vm.studentId,
                    { id: vm.team.id, subgroupId: vm.subgroup.id },
                    vm.currentTeam || null
                )

                vm.$emit('save', vm.studentId)
                vm.hide()
            })
        },
        setDefaults() {
            const vm = this
            if (!vm.currentTeam) return
            vm.team = { id: vm.findTeam?.id || null, ...vm.findTeam }
            let findSubgroup = vm.team?.groups?.find(group => group.id === vm.currentTeam.subgroupId) || null
            if (!findSubgroup) return
            vm.subgroup = { id: findSubgroup.id, ...findSubgroup }
        },
        onCancelReplace() {
            const vm = this
            vm.loading = false
        }
    },
    watch: {
        currentTeam() {
            this.setDefaults()
        },
        team(val) {
            if (this.currentTeam?.id !== val?.id) this.subgroup = null
        },
    }
}
</script>

<style scoped>
.team-selects {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
</style>
